import { computed, ref, watch, type Ref } from 'vue';

import {
  ATTACHMENT_EXTENSIONS,
  INVALID_ATTACHMENT_FILE_MESSAGE,
  MAX_DOCUMENT_SEND_ATTACHMENTS,
} from '@/shared/config';
import {
  formatUrl,
  hasAllowedAttachmentExtension,
  useFileDropzone,
  validateUrl,
} from '@/shared/lib';

export type ElectronicSendMode = 'upload' | 'url' | '';

export interface LocalUrl {
  rowId?: string;
  name: string;
  url: string;
}

let urlRowSequence = 0;

const nextUrlRowSequenceId = (): string => {
  urlRowSequence += 1;

  return `url-row-${urlRowSequence}`;
};

export const createUrlRowId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : nextUrlRowSequenceId();

export const MAX_TOTAL_BYTES = 10_485_760;
export const ALLOWED_EXTENSIONS_LABEL = ATTACHMENT_EXTENSIONS.join(' | ');
export const TOTAL_SIZE_EXCEEDED_MESSAGE =
  'O total de anexos ultrapassa 10MB. Remova arquivos para continuar.';
export const buildTooManyAttachmentsMessage = (maxAttachments: number): string =>
  `O número máximo total de anexos por envio é ${maxAttachments}.`;

export const TOO_MANY_ATTACHMENTS_MESSAGE = buildTooManyAttachmentsMessage(
  MAX_DOCUMENT_SEND_ATTACHMENTS
);

export const MAX_URL_NAME_LENGTH = 255;
export const MAX_URL_LENGTH = 2084;
export const EMPTY_URL_MESSAGE = 'Informe a URL do documento.';
export const URL_TOO_LONG_MESSAGE = `URL muito longa (máximo de ${MAX_URL_LENGTH} caracteres).`;
export const URL_ROWS_REJECTED_MESSAGE = 'Verifique as URLs destacadas e tente novamente.';
export const DUPLICATE_URL_MESSAGE = 'Cada URL deve ser informada apenas uma vez por envio.';

const GENERIC_URL_SEGMENTS = new Set([
  'view',
  'download',
  'preview',
  'edit',
  'open',
  'file',
  'files',
  'index',
  'uc',
  'd',
  'doc',
  'docs',
  'document',
  'documents',
]);

const FILE_SEGMENT_PATTERN = /\.[a-z0-9]{2,5}$/i;

const ENCODABLE_CHARACTERS_PATTERN = /[\s<>]/;

const URL_FIELD_ERROR_PATTERN = /^localUrls\.(\d+)\.(url|name)$/;

const CONTROL_CHARACTERS_PATTERN = /\p{C}/gu;

const decodeUrlPart = (part: string): string => {
  try {
    return decodeURIComponent(part);
  } catch {
    return part;
  }
};

const sanitizeUrlNamePart = (part: string): string =>
  decodeUrlPart(part).replace(CONTROL_CHARACTERS_PATTERN, ' ').replace(/\s+/g, ' ').trim();

export const canonicalizeUrl = (rawUrl: string): string => {
  const formattedUrl = formatUrl(rawUrl);

  if (!formattedUrl || !ENCODABLE_CHARACTERS_PATTERN.test(formattedUrl)) {
    return formattedUrl;
  }

  try {
    return new URL(formattedUrl).href;
  } catch {
    return formattedUrl;
  }
};

export const canonicalizeUrlWhenValid = (rawUrl: string): string => {
  const canonicalUrl = canonicalizeUrl(rawUrl);

  return validateUrl(canonicalUrl).error === '' ? canonicalUrl : rawUrl;
};

export const validateUrlValue = (rawUrl: string): string => {
  const canonicalUrl = canonicalizeUrl(rawUrl);
  const formatError = validateUrl(canonicalUrl).error;

  if (formatError !== '') {
    return formatError;
  }

  return canonicalUrl.length > MAX_URL_LENGTH ? URL_TOO_LONG_MESSAGE : '';
};

const toCanonicalUrlKey = (rawUrl: string): string | null => {
  const canonicalUrl = canonicalizeUrlWhenValid(rawUrl);

  if (validateUrlValue(canonicalUrl) !== '') {
    return null;
  }

  try {
    return new URL(canonicalUrl).href;
  } catch {
    return canonicalUrl;
  }
};

export const deriveUrlName = (rawUrl: string): string => {
  const canonicalUrl = canonicalizeUrl(rawUrl);

  if (!canonicalUrl) {
    return '';
  }

  let hostname = '';
  let pathSegments: string[] = [];
  let queryNames: string[] = [];

  try {
    const url = new URL(canonicalUrl);

    hostname = url.hostname;
    pathSegments = url.pathname
      .split('/')
      .map(sanitizeUrlNamePart)
      .filter((segment) => segment !== '')
      .reverse();
    queryNames = [...url.searchParams.values()]
      .map((value) => sanitizeUrlNamePart(value).split('/').filter(Boolean).pop() ?? '')
      .filter((value) => value !== '');
  } catch {
    return canonicalUrl.slice(0, MAX_URL_NAME_LENGTH);
  }

  const candidates = [
    pathSegments.find((segment) => FILE_SEGMENT_PATTERN.test(segment)),
    queryNames.find((value) => FILE_SEGMENT_PATTERN.test(value)),
    pathSegments.find((segment) => !GENERIC_URL_SEGMENTS.has(segment.toLowerCase())),
    hostname,
    canonicalUrl,
  ];

  return (candidates.find((candidate) => candidate !== undefined && candidate !== '') ?? '').slice(
    0,
    MAX_URL_NAME_LENGTH
  );
};

const selectNormalizableUrlRows = (
  urls: LocalUrl[]
): Array<{ index: number; row: LocalUrl; url: string }> =>
  urls
    .map((localUrl, index) => ({
      index,
      row: localUrl,
      url: canonicalizeUrlWhenValid(localUrl.url),
    }))
    .filter(({ url }) => url !== '');

export const normalizeLocalUrls = (urls: LocalUrl[]): LocalUrl[] =>
  selectNormalizableUrlRows(urls).map(({ row, url }) => ({
    ...row,
    name: deriveUrlName(url),
    url,
  }));

export const selectLocalUrls = (mode: ElectronicSendMode, urls: LocalUrl[]): LocalUrl[] =>
  mode === 'url' ? urls.filter((localUrl) => localUrl.url.trim() !== '') : [];

export const selectLocalFiles = (mode: ElectronicSendMode, files: File[]): File[] =>
  mode === 'url' ? [] : files;

export const countLocalUrls = (mode: ElectronicSendMode, urls: LocalUrl[]): number =>
  selectLocalUrls(mode, urls).length;

export const countLocalFiles = (mode: ElectronicSendMode, files: File[]): number =>
  selectLocalFiles(mode, files).length;

export const validateLocalUrls = (
  urls: LocalUrl[],
  availableSlots?: number,
  limitMessage: string = TOO_MANY_ATTACHMENTS_MESSAGE
): string[] => {
  let filledCount = 0;
  const seenCanonicalUrls = new Set<string>();

  return urls.map((localUrl) => {
    if (localUrl.url.trim() === '') {
      return EMPTY_URL_MESSAGE;
    }

    filledCount += 1;

    if (availableSlots !== undefined && filledCount > availableSlots) {
      return limitMessage;
    }

    const urlError = validateUrlValue(localUrl.url);

    if (urlError !== '') {
      return urlError;
    }

    const canonicalUrlKey = toCanonicalUrlKey(localUrl.url);

    if (canonicalUrlKey !== null && seenCanonicalUrls.has(canonicalUrlKey)) {
      return DUPLICATE_URL_MESSAGE;
    }

    if (canonicalUrlKey !== null) {
      seenCanonicalUrls.add(canonicalUrlKey);
    }

    return '';
  });
};

export const resolveRejectedUrlRows = (
  urls: LocalUrl[],
  fieldErrors: Record<string, string[]>
): string[] | null => {
  const rowIndexByPayloadIndex = selectNormalizableUrlRows(urls).map(({ index }) => index);
  const rowErrors = urls.map(() => '');
  let hasRejectedRow = false;

  Object.entries(fieldErrors).forEach(([field, messages]) => {
    const match = URL_FIELD_ERROR_PATTERN.exec(field);
    const message = messages[0];

    if (!match || message === undefined) {
      return;
    }

    const rowIndex = rowIndexByPayloadIndex[Number(match[1])];

    if (rowIndex === undefined || rowErrors[rowIndex] !== '') {
      return;
    }

    rowErrors[rowIndex] = message;
    hasRejectedRow = true;
  });

  return hasRejectedRow ? rowErrors : null;
};

export const buildLocalUrlsPayload = (mode: ElectronicSendMode, urls: LocalUrl[]): LocalUrl[] =>
  mode === 'url' ? normalizeLocalUrls(urls).map(({ name, url }) => ({ name, url })) : [];

type AttachmentProps = {
  selectedElectronicMode: ElectronicSendMode;
  message: string;
  gdAttachmentCount: number;
  gdDocumentsTotalBytes: number;
  localFiles: File[];
  localUrls: LocalUrl[];
  urlErrors: string[];
};

type AttachmentEmit = {
  (event: 'update:selectedElectronicMode', value: ElectronicSendMode): void;
  (event: 'update:message', value: string): void;
  (event: 'update:localFiles', value: File[]): void;
  (event: 'update:localUrls', value: LocalUrl[]): void;
  (event: 'update:urlErrors', value: string[]): void;
};

const isAllowedFile = (file: File) => hasAllowedAttachmentExtension(file.name);

export function useElectronicAttachments(props: AttachmentProps, emit: AttachmentEmit) {
  const sizeError = ref('');
  const { fileInput, isDragging, openFileDialog, onDragEnter, onDragLeave, onDragOver, onDrop } =
    useFileDropzone();

  const stagedUrls = ref<LocalUrl[] | null>(null);
  const stagedUrlErrors = ref<string[] | null>(null);

  const currentUrls = computed(() => stagedUrls.value ?? props.localUrls);
  const currentUrlErrors = computed(() => stagedUrlErrors.value ?? props.urlErrors);

  watch([() => props.localUrls, () => props.urlErrors], () => {
    stagedUrls.value = null;
    stagedUrlErrors.value = null;
  });

  const commitUrls = (urls: LocalUrl[], errors: string[]) => {
    stagedUrls.value = urls;
    stagedUrlErrors.value = errors;
    emit('update:localUrls', urls);
    emit('update:urlErrors', errors);
  };

  const electronicMode = computed({
    get: () => props.selectedElectronicMode,
    set: (value: ElectronicSendMode) => emit('update:selectedElectronicMode', value),
  });

  const electronicMessage = computed({
    get: () => props.message,
    set: (value: string) => emit('update:message', value),
  });

  const totalAttachments = computed(
    () =>
      props.gdAttachmentCount +
      countLocalFiles(props.selectedElectronicMode, props.localFiles) +
      countLocalUrls(props.selectedElectronicMode, currentUrls.value)
  );
  const isAtAttachmentLimit = computed(
    () => totalAttachments.value >= MAX_DOCUMENT_SEND_ATTACHMENTS
  );
  const isOverAttachmentLimit = computed(
    () => totalAttachments.value > MAX_DOCUMENT_SEND_ATTACHMENTS
  );
  const localFilesTotalBytes = computed(() =>
    props.localFiles.reduce((sum, file) => sum + file.size, 0)
  );
  const totalBytes = computed(() => props.gdDocumentsTotalBytes + localFilesTotalBytes.value);

  const triggerFileInput = () => {
    sizeError.value = '';
    openFileDialog();
  };

  const addFiles = (selected: File[]) => {
    sizeError.value = '';

    if (selected.length === 0) {
      return;
    }

    if (selected.some((file) => !isAllowedFile(file))) {
      sizeError.value = INVALID_ATTACHMENT_FILE_MESSAGE;
      return;
    }

    if (totalAttachments.value + selected.length > MAX_DOCUMENT_SEND_ATTACHMENTS) {
      sizeError.value = TOO_MANY_ATTACHMENTS_MESSAGE;
      return;
    }

    const selectedBytes = selected.reduce((sum, file) => sum + file.size, 0);
    if (totalBytes.value + selectedBytes > MAX_TOTAL_BYTES) {
      sizeError.value = TOTAL_SIZE_EXCEEDED_MESSAGE;
      return;
    }

    emit('update:localFiles', [...props.localFiles, ...selected]);
  };

  const onFilesSelected = (event: Event) => {
    const input = event.target as HTMLInputElement;
    addFiles(Array.from(input.files ?? []));
    input.value = '';
  };

  const onFilesDropped = (event: DragEvent) => {
    const droppedFiles = onDrop(event);
    addFiles(Array.from(droppedFiles ?? []));
  };

  const removeFile = (index: number) => {
    emit(
      'update:localFiles',
      props.localFiles.filter((_, fileIndex) => fileIndex !== index)
    );
    sizeError.value = '';
  };

  const replaceUrlAt = (index: number, url: string) =>
    currentUrls.value.map((localUrl, urlIndex) =>
      urlIndex === index ? { ...localUrl, name: deriveUrlName(url), url } : localUrl
    );

  const alignedUrlErrors = () =>
    currentUrls.value.map((_, urlIndex) => currentUrlErrors.value[urlIndex] ?? '');

  const replaceUrlErrorAt = (index: number, error: string) =>
    alignedUrlErrors().map((currentError, urlIndex) => (urlIndex === index ? error : currentError));

  const addUrlRow = () => {
    if (totalAttachments.value >= MAX_DOCUMENT_SEND_ATTACHMENTS) {
      return;
    }

    commitUrls(
      [...currentUrls.value, { rowId: createUrlRowId(), name: '', url: '' }],
      [...alignedUrlErrors(), '']
    );
  };

  const updateUrl = (index: number, value: string) => {
    commitUrls(replaceUrlAt(index, value), replaceUrlErrorAt(index, ''));
  };

  const blurUrl = (index: number) => {
    const currentUrl = currentUrls.value[index]?.url ?? '';
    const nextUrl = canonicalizeUrlWhenValid(currentUrl);
    const nextError = validateUrlValue(nextUrl);

    if (nextUrl === currentUrl && nextError === (currentUrlErrors.value[index] ?? '')) {
      return;
    }

    commitUrls(replaceUrlAt(index, nextUrl), replaceUrlErrorAt(index, nextError));
  };

  const removeUrl = (index: number) => {
    commitUrls(
      currentUrls.value.filter((_, urlIndex) => urlIndex !== index),
      alignedUrlErrors().filter((_, urlIndex) => urlIndex !== index)
    );
  };

  return {
    electronicMessage,
    electronicMode,
    fileInput: fileInput as Ref<HTMLInputElement | null>,
    isDragging,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onFilesDropped,
    onFilesSelected,
    openFileDialog: triggerFileInput,
    removeFile,
    removeUrl,
    sizeError,
    totalAttachments,
    isAtAttachmentLimit,
    isOverAttachmentLimit,
    tooManyAttachmentsMessage: TOO_MANY_ATTACHMENTS_MESSAGE,
    addUrlRow,
    updateUrl,
    blurUrl,
  };
}
